import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import {
  getCarouselMedia,
  getCarouselsStatus,
  getCarouselsError,
  updateCarouselOrder,
  updateStatus,
  getCarouselsSuccess,
  deleteCarouselMedia,
} from "../../../features/carouselSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import Loading from "../../../components/loading";
import AlertDialog from "../../../components/alertDialog";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import BackButton from "../../../components/backButton";
import { useTranslation } from "react-i18next";

const apiUrl = process.env.REACT_APP_MEDIA_URL;

const SortableItem = ({ id, order, onDelete, deleteLabel, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        aspectRatio: "1",
        bgcolor: "action.hover",
        boxShadow: isDragging ? 4 : 1,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
      }}
    >
      {children}
      <Box
        sx={{
          position: "absolute",
          top: 6,
          left: 6,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: "rgba(0,0,0,0.6)",
          color: "white",
          fontSize: 12,
          fontWeight: 600,
          pointerEvents: "none",
        }}
      >
        #{order}
      </Box>
      <Tooltip title={deleteLabel}>
        <IconButton
          onClick={onDelete}
          onPointerDown={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            bgcolor: "rgba(255,255,255,0.9)",
            width: 30,
            height: 30,
            "&:hover": { bgcolor: "white" },
          }}
        >
          <DeleteOutlineIcon sx={{ color: "error.main", fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ModifyCarousel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [mediaId, setMediaId] = useState("");
  const carouselItems = useSelector((state) => state.carousel?.items || []);
  const status = useSelector(getCarouselsStatus);
  const error = useSelector(getCarouselsError);
  const success = useSelector(getCarouselsSuccess);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const handleOpen = (id) => {
    setOpen(true);
    setMediaId(id);
  };

  const handleDelete = (id) => {
    dispatch(deleteCarouselMedia({ id, restaurantId }));
    setOpen(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item._id === active.id);
    const newIndex = items.findIndex((item) => item._id === over.id);
    const newOrder = arrayMove(items, oldIndex, newIndex);
    setItems(newOrder);

    const updatedItems = newOrder.map((item, index) => ({
      id: item._id,
      order: index + 1,
    }));
    dispatch(updateCarouselOrder({ items: updatedItems, restaurantId }));
  };

  useEffect(() => {
    if (status === "deleteSuccess") {
      const updatedItems = items.filter((item) => item._id !== mediaId);
      setItems(updatedItems);
      setMediaId("");
      toast.success(success);
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      setMediaId("");
      toast.error(error);
    }
  }, [status, dispatch, error, success, items, mediaId]);

  useEffect(() => {
    if (status === "updateSuccess") {
      toast.success(success);
      dispatch(updateStatus());
    } else if (status === "updateError") {
      toast.error(error);
    }
  }, [status, dispatch, error, success]);

  useEffect(() => {
    if (status === "fetchData") {
      setItems(carouselItems);
    }
  }, [status, carouselItems]);

  useEffect(() => {
    if (status === "idle" && items.length === 0) {
      dispatch(getCarouselMedia(restaurantId));
    }
  }, [dispatch, status, items.length, restaurantId]);

  return (
    <div className="main-application">
      <BackButton />
      <Header
        title={t("headers.editCarousel")}
        subtitle={t("headers.editCarouselSub")}
      />
      <main>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("carousel.dragHint")}
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            onClick={() => navigate("/carousel/add")}
          >
            {t("actions.addMedia")}
          </Button>
        </Box>

        {status === "loadingCarousel" ? (
          <Loading />
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 6,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <AddPhotoAlternateOutlinedIcon
              sx={{ fontSize: 40, color: "text.secondary" }}
            />
            <Typography color="text.secondary">
              {t("carousel.emptyState")}
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item._id)}
              strategy={verticalListSortingStrategy}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 2,
                }}
              >
                {items.map((item, index) => (
                  <SortableItem
                    key={item._id}
                    id={item._id}
                    order={index + 1}
                    onDelete={() => handleOpen(item._id)}
                    deleteLabel={t("common.delete")}
                  >
                    {item.mediaType === "image" ? (
                      <img
                        src={`${apiUrl}/${item.fileUrl}`}
                        alt="carousel"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          pointerEvents: "none",
                        }}
                      />
                    ) : (
                      <video
                        src={`${apiUrl}/${item.fileUrl}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          pointerEvents: "none",
                        }}
                        onDragStart={(e) => e.preventDefault()}
                      />
                    )}
                  </SortableItem>
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}

        <AlertDialog
          handleClose={() => setOpen(false)}
          open={open}
          message={t("carousel.deleteConfirm")}
          cardId={mediaId}
          deleteData={() => handleDelete(mediaId)}
        />
      </main>
    </div>
  );
};

export default ModifyCarousel;
